// DocumentsPage.jsx — rewritten
import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import './DocumentsPage.css';

// Modals / tabs
import OrderViewModal from './modals/OrderViewModal';
import CreateContractModal from './modals/CreateContractModal';
import ContractsModal from './modals/ContractsModal';
import ViewContractModal from './modals/ViewContractModal';
import OrdersTab from './OrdersTab';
import ContractsTab from './ContractsTab';

// (пока не используются в этом файле, но оставлены если применяются по месту)
import ContractAssignModal from './modals/ContractAssignModal';
import ContractsListModal from './modals/ContractsListModal';
import ContractViewModal from './modals/ContractViewModal';

/* ================= helpers ================= */
const norm = (v) => String(v ?? '').trim().toLowerCase();

const isExternalExpert = (p) => {
  if (!p) return false;
  const EXTERNAL_EXPERT_ROLE_IDS = []; // например: [45]
  const id = Number(p.role?.id);
  if (EXTERNAL_EXPERT_ROLE_IDS.includes(id)) return true;

  const fields = [
    p.role?.name, p.role?.slug, p.role?.title, p.role?.label,
    p.position, p.title, p.user_role, p.user_role_slug, p.userRole,
  ]
    .filter(Boolean)
    .map(norm);

  const many = []
    .concat(p.roles || [], p.user_roles || [])
    .map((r) => (typeof r === 'string' ? r : r?.name || r?.slug || r?.title))
    .filter(Boolean)
    .map(norm);

  const hasEE = (s) =>
    s.includes('external_expert') ||
    (s.includes('внешн') && s.includes('эксперт')) ||
    (s.includes('сыртқы') && s.includes('сарапшы'));

  return [...fields, ...many].some(hasEE);
};

export default function DocumentsApplicationsList({ locale }) {
  /* ================= i18n ================= */
  const t = useMemo(() => ({
    kz: {
      title: 'Бекітілген құжаттар',
      title2: 'Қосымша',
      noGroups: 'Топтар табылмады',
      createOrder: 'Бұйрықты жасау',
      viewOrder: 'Бұйрықты көру',
      orderTitle: 'Тақырып:',
      orderDesc: 'Сипаттама:',
      orderStatus: 'Күйі:',
      applications: 'Өтінімдер:',
      signers: 'Қол қою реті:',
      create: 'Құру',
      searchByGroup: 'Топ атауы бойынша іздеу',
      createContract: 'Келісім шарт жасау',
      contractList: 'Келісім шарт тізімі',
      submitOrder: 'Жіберілуде...',
      fillAllFields: 'Барлық өрістерді толтырыңыз.',
      orderCreated: '📄 Бұйрық сәтті құрылды!',
      orderError: 'Қате орын алды. Қайта байқап көріңіз.',
      orderViewError: 'Қолжетімсіз немесе қате шықты.',
      loadingError: 'Мәліметтерді жүктеу қатесі',
      contractSuccess: '✅ Келісім-шарт жасалды',
      contractError: '❌ Келісім-шарт жасау кезінде қате болды',
      orderNotFound: 'Бұйрық анықталмады',
      tableHeaders: {
        title: 'Оқу басылымы',
        grade: 'Сынып',
        category: 'Санат',
        language: 'Әзірлеу тілі',
        publisher: 'Баспа',
        status: 'Күйі',
      },
      tabs: { orders: 'Бұйрықтар', contracts: 'Келісімшарттар' },
      backToList: 'Тізімге қайту',
      loading: 'Жүктелуде...'
    },
    ru: {
      title: 'Утвержденные документы',
      title2: 'Дополнение',
      noGroups: 'Группы не найдены',
      createOrder: 'Создать приказ',
      viewOrder: 'Просмотр приказа',
      orderTitle: 'Заголовок:',
      orderDesc: 'Описание:',
      orderStatus: 'Статус:',
      applications: 'Заявки:',
      signers: 'Порядок подписания:',
      create: 'Создать',
      searchByGroup: 'Поиск по названию группы',
      createContract: 'Создать договор',
      contractList: 'Список договоров',
      submitOrder: 'Отправка...',
      fillAllFields: 'Пожалуйста, заполните все поля.',
      orderCreated: '📄 Приказ успешно создан!',
      orderError: 'Произошла ошибка. Попробуйте снова.',
      orderViewError: 'Недоступно или произошла ошибка.',
      loadingError: 'Ошибка при загрузке данных',
      contractSuccess: '✅ Договор создан',
      contractError: '❌ Ошибка при создании договора',
      orderNotFound: 'Приказ не определен',
      tableHeaders: {
        title: 'Учебное издание',
        grade: 'Класс',
        category: 'Категория',
        language: 'Язык разработки',
        publisher: 'Издательство',
        status: 'Статус',
      },
      tabs: { orders: 'Приказы', contracts: 'Договоры' },
      backToList: 'Вернуться к списку',
      loading: 'Загрузка...'
    },
  })[locale], [locale]);

  const langHeader = useMemo(
    () => ({ 'Accept-Language': locale === 'kz' ? 'kk' : 'ru' }),
    [locale]
  );

  /* ================= state ================= */
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const [generatingGroupId, setGeneratingGroupId] = useState(null);
  const [orderForm, setOrderForm] = useState({ title: '', title2: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  const [contractApplications, setContractApplications] = useState([]);
  const [contractList, setContractList] = useState([]);
  const [contractExperts, setContractExperts] = useState([]);
  const [contractsModalVisible, setContractsModalVisible] = useState(false);
  const [contractModalVisible, setContractModalVisible] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(false);

  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });

  const [groupFilter, setGroupFilter] = useState('');
  const [localPage, setLocalPage] = useState(1);

  // 'orders' | 'contracts' | null (null = экран списка групп)
  const [activeTab, setActiveTab] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';

  /* ================= data loaders ================= */
  const fetchProfile = async () => {
    try {
      setLoadingProfile(true);
      const res = await axios.get('/internal/profile', {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
      });
      setProfile(res.data);
      return res.data;
    } catch (err) {
      setProfile(null);
      return null;
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchGroups = async (page = 1) => {
    try {
      setLoadingGroups(true);
      const res = await axios.get('/documents-applications', {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
        params: { page },
      });

      setGroups(Array.isArray(res.data) ? res.data : res.data.data || []);
      if (res.data.current_page) {
        setPagination({ current_page: res.data.current_page, last_page: res.data.last_page });
      }
    } catch (err) {
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const res = await axios.get(`/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
      });
      setOrderDetails(res.data);
    } catch (err) {
      alert(t.orderViewError);
    }
  };

  /* ================= init / reactions ================= */
  // Initial load
  useEffect(() => {
    (async () => {
      const prof = await fetchProfile();
      if (prof && isExternalExpert(prof)) {
        setActiveTab('contracts'); // внешнему — сразу вкладка договоров
      } else {
        setActiveTab('orders'); // остальным — вкладка приказов
        await fetchGroups(1);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to locale change: reload profile (и группы для невнешних)
  useEffect(() => {
    (async () => {
      const prof = await fetchProfile();
      if (!isExternalExpert(prof)) {
        await fetchGroups(pagination.current_page || 1);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);



  /* ================= orders ================= */
  const handleGenerateOrder = async () => {
    if (!orderForm.title || !orderForm.title2 || !orderForm.description) {
      alert(t.fillAllFields);
      return;
    }
    if (!generatingGroupId) return;

    setSubmitting(true);
    try {
      await axios.post(
        `/orders/${generatingGroupId}/generate`,
        { title: orderForm.title, title2: orderForm.title2, description: orderForm.description },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(t.orderCreated);
      setGeneratingGroupId(null);
      setOrderForm({ title: '', title2: '', description: '' });
      await fetchGroups(pagination.current_page || 1);
    } catch (err) {
      alert(t.orderError);
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= contracts ================= */
  const [contractData, setContractData] = useState({
    external_expert_id: '',
    amount_numeric: '',
    amount_text_kz: '',
    amount_text_ru: '',
    baspa_tabaks: [''],
    durations: [''],
    page_counts: [''],
  });

  const openContractModal = async (orderId) => {
    try {
      const res = await axios.get(`/orders/${orderId}/applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const applications = res.data.applications || [];
      const expertList = res.data.external_experts || [];
      const initialArray = Array.isArray(applications) ? applications.map(() => '') : [];

      setCurrentOrderId(orderId);
      setContractExperts(expertList);
      setContractApplications(applications);
      setContractData({
        external_expert_id: '',
        amount_numeric: '',
        amount_text_kz: '',
        amount_text_ru: '',
        baspa_tabaks: [...initialArray],
        durations: [...initialArray],
        page_counts: [...initialArray],
      });
      setContractModalVisible(true);
    } catch (err) {
      alert(t.loadingError);
    }
  };

  const openContractsModal = async (orderId) => {
    try {
      const res = await axios.get('/contracts', {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
        params: { order_id: orderId },
      });
      setContractList(res.data);
      setContractsModalVisible(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Қате орын алды');
    }
  };

  const openContractView = async (contractId) => {
    try {
      const res = await axios.get(`/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedContract(res.data);
      setViewingContract(true);
    } catch (err) {
      alert(err.response?.data?.message || t.loadingError);
    }
  };

  const handleCreateContract = async () => {
    try {
      if (!currentOrderId) {
        alert(t.orderNotFound);
        return;
      }
      const payload = {
        external_expert_id: Number(contractData.external_expert_id),
        amount_numeric: Number(contractData.amount_numeric),
        amount_text_kz: contractData.amount_text_kz,
        amount_text_ru: contractData.amount_text_ru,
        baspa_tabaks: contractData.baspa_tabaks.map(String),
        durations: contractData.durations.map(String),
        page_counts: contractData.page_counts.map(String),
      };
      const res = await axios.post(
        `/orders/${currentOrderId}/generate-contract`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, ...langHeader } }
      );
      alert(res.data.message || t.contractSuccess);
      setContractModalVisible(false);
    } catch (err) {
      const msg = err.response?.data?.message || 'Қате орын алды';
      alert(`❌ ${msg || t.contractError}\n\n${err.response?.data?.error || ''}`);
    }
  };

  /* ================= computed ================= */
  const filteredGroups = useMemo(() =>
    groups.filter((g) => g.title?.toLowerCase().includes(groupFilter.toLowerCase())),
  [groups, groupFilter]);

  const groupsPerPage = 4;
  const totalLocalPages = Math.ceil(filteredGroups.length / groupsPerPage) || 1;
  const paginatedGroups = useMemo(() =>
    filteredGroups.slice((localPage - 1) * groupsPerPage, localPage * groupsPerPage),
  [filteredGroups, localPage]);

  const roleName = (profile?.role?.name || '').toLowerCase();
  const isEmployee = profile?.role?.id === 2 || roleName === 'сотрудник';
  const isThatDept = profile?.department?.name === 'Сараптаманы жоспарлау және мемлекеттік қызметтер бөлімі';
  const forbidViewOrder = roleName === 'external_expert' || isEmployee || isThatDept;

  /* ================= ui helpers ================= */
  function renderPager({ total, current, onGo, delta = 1 }) {
    if (!total || total <= 1) return null;
    const items = [];
    items.push(1);
    if (current - delta > 2) items.push('...');
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) items.push(i);
    if (current + delta < total - 1) items.push('...');
    if (total > 1) items.push(total);

    const Btn = ({ active, children, onClick, disabled }) => (
      <button
        onClick={onClick}
        disabled={disabled}
        style={{
          padding: '8px 14px',
          minWidth: 38,
          backgroundColor: active ? '#1d4e89' : '#fff',
          color: active ? '#fff' : '#333',
          border: `1px solid ${active ? '#1d4e89' : '#ccc'}`,
          borderRadius: 6,
          fontWeight: active ? 600 : 400,
          fontSize: 14,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {children}
      </button>
    );

    return (
      <div style={{ marginTop: 30, textAlign: 'center', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 6 }}>
        <Btn onClick={() => onGo(Math.max(1, current - 1))} disabled={current === 1}>‹</Btn>
        {items.map((p, idx) => p === '...'
          ? <span key={`dots-${idx}`} style={{ padding: '8px 14px', color: '#999' }}>…</span>
          : <Btn key={p} active={current === p} onClick={() => onGo(p)}>{p}</Btn>)
        }
        <Btn onClick={() => onGo(Math.min(total, current + 1))} disabled={current === total}>›</Btn>
      </div>
    );
  }

  /* ================= render ================= */
  return (
    <>
      <h2>📑 {t.title}</h2>

      {/* TAB BAR — показываем только после загрузки профиля */}
      {loadingProfile ? (
        <p style={{ opacity: 0.8 }}>{t.loading}</p>
      ) : (
        <div style={{ display: 'flex', gap: 10, margin: '6px 0 16px' }}>
          {(profile && isExternalExpert(profile) ? ['contracts'] : ['orders', 'contracts']).map((key) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={activeTab === key ? 'tab-active' : 'tab'}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: activeTab === key ? '2px solid #1d4e89' : '1px solid #d1d5db',
                background: activeTab === key ? '#1d4e89' : '#fff',
                color: activeTab === key ? '#fff' : '#111827',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {key === 'orders' ? t.tabs.orders : t.tabs.contracts}
            </button>
          ))}

          {/* Кнопка возврата к списку — скрыта для внешнего эксперта */}
          {activeTab && profile && !isExternalExpert(profile) && (
            <button
              onClick={() => setActiveTab(null)}
              className="btn-secondary"
              style={{ marginLeft: 'auto' }}
            >
              {t.backToList}
            </button>
          )}
        </div>
      )}

      {/* Экран списка групп (виден только когда activeTab === null) */}
      {activeTab === null && !isExternalExpert(profile) && (
        <>
          <input
            type="text"
            placeholder={t.searchByGroup}
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            style={{
              margin: '10px 0 20px',
              padding: '8px',
              width: '300px',
              borderRadius: '5px',
              border: '1px solid #ccc',
              fontSize: '14px',
            }}
          />

          {loadingGroups && <p style={{ opacity: 0.8 }}>{t.loading}</p>}
          {!loadingGroups && filteredGroups.length === 0 && (
            <p style={{ opacity: 0.8 }}>{t.noGroups}</p>
          )}

          {paginatedGroups.map((group) => (
            <div key={group.id} className="group-section">
              <h3>{group.title}</h3>

              <table className="custom-table">
                <thead>
                  <tr>
                    <th>{t.tableHeaders.title}</th>
                    <th>{t.tableHeaders.grade}</th>
                    <th>{t.tableHeaders.category}</th>
                    <th>{t.tableHeaders.language}</th>
                    <th>{t.tableHeaders.publisher}</th>
                    <th>{t.tableHeaders.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.applications?.map((app) => (
                    <tr key={app.id}>
                      <td>{app.title}</td>
                      <td>{app.grade}</td>
                      <td>{app.category}</td>
                      <td>{app.development_language}</td>
                      <td>{app.publisher}</td>
                      <td>{app.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Кнопки действий здесь не зависят от activeTab (мы на экране списка) */}
              {profile && (
                <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {/* Заказы */}
                  {!group.order_id && roleName === 'internal_expert' && (
                    <button className="btn-primary" onClick={() => setGeneratingGroupId(group.id)}>
                      📝 {t.createOrder}
                    </button>
                  )}
                  {group.order_id && !forbidViewOrder && (
                    <button className="btn-secondary" onClick={() => fetchOrderDetails(group.order_id)}>
                      {t.viewOrder}
                    </button>
                  )}

                  {/* Договоры */}
                  {group.order_id && (
                    <>
                      {roleName === 'internal_expert' && group.order_status?.toLowerCase() === 'ready' && (
                        <button className="btn-primary" onClick={() => openContractModal(group.order_id)}>
                          📑 {t.createContract}
                        </button>
                      )}

                      {[
                        'internal_expert',
                        'department_supervisor',
                        'moderator_procurement',
                        'deputy_director_2 ',
                        'moderator_accountant',
                        'директор',
                        'moderator_registrar',
                        'external_expert',
                      ].includes(roleName) && (
                        <button className="btn-secondary" onClick={() => openContractsModal(group.order_id)}>
                          📁 {t.contractList}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          {pagination.last_page > 1 &&
            renderPager({
              total: pagination.last_page,
              current: pagination.current_page,
              onGo: (p) => {
                fetchGroups(p);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              },
              delta: 1,
            })}

          {/* Локальная пагинация по отфильтрованным (если нужно) */}
          {totalLocalPages > 1 &&
            renderPager({ total: totalLocalPages, current: localPage, onGo: setLocalPage, delta: 1 })}
        </>
      )}

      {/* Вкладки */}
      {activeTab === 'orders' && !isExternalExpert(profile) && (
        <OrdersTab locale={locale} profile={profile} />
      )}

      {activeTab === 'contracts' && (
        <ContractsTab locale={locale} profile={profile} />
      )}

      {/* === модалки === */}
      {generatingGroupId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              width: '500px',
              maxWidth: '90%',
              borderRadius: '8px',
              boxShadow: '0 0 10px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              fontFamily: 'Segoe UI, sans-serif',
            }}
          >
            <div
              style={{
                backgroundColor: '#1d4e89',
                color: 'white',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 'bold',
              }}
            >
              <h3 style={{ margin: 0 }}>📄 {t.createOrder}</h3>
              <button
                onClick={() => setGeneratingGroupId(null)}
                style={{ background: 'none', border: 'none', fontSize: '24px', color: 'white', cursor: 'pointer' }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: '16px' }}>
              <label style={{ display: 'block', marginTop: 12, marginBottom: 6, fontWeight: 500, color: '#333' }}>
                {t.orderTitle}
              </label>
              <input
                type="text"
                value={orderForm.title}
                onChange={(e) => setOrderForm({ ...orderForm, title: e.target.value })}
                style={{ width: '100%', padding: 10, border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }}
              />

              <label style={{ display: 'block', marginTop: 12, marginBottom: 6, fontWeight: 500, color: '#333' }}>
                {t.orderDesc}
              </label>
              <textarea
                rows="4"
                value={orderForm.description}
                onChange={(e) => setOrderForm({ ...orderForm, description: e.target.value })}
                style={{ width: '100%', padding: 10, border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box', resize: 'none' }}
              />

              <label style={{ display: 'block', marginTop: 12, marginBottom: 6, fontWeight: 500, color: '#333' }}>
                {t.title2}
              </label>
              <textarea
                rows="2"
                value={orderForm.title2}
                onChange={(e) => setOrderForm({ ...orderForm, title2: e.target.value })}
                style={{ width: '100%', padding: 10, border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box', resize: 'none' }}
              />
            </div>

            <div style={{ padding: 16, textAlign: 'right', backgroundColor: '#f9f9f9' }}>
              <button
                onClick={handleGenerateOrder}
                disabled={submitting}
                style={{
                  backgroundColor: submitting ? '#9bb1ce' : '#1d4e89',
                  color: 'white',
                  border: 'none',
                  padding: '10px 18px',
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'background .3s',
                }}
              >
                {submitting ? t.submitOrder : t.create}
              </button>
            </div>
          </div>
        </div>
      )}

      {orderDetails && (
        <OrderViewModal
          orderDetails={orderDetails}
          onClose={() => setOrderDetails(null)}
          locale={locale}
          profile={profile}
          orderForm={orderForm}
          setOrderForm={setOrderForm}
          fetchOrderDetails={fetchOrderDetails}
          fetchGroups={fetchGroups}
          token={token}
        />
      )}

      {contractModalVisible && (
        <CreateContractModal
          locale={locale}
          contractExperts={contractExperts}
          contractApplications={contractApplications}
          contractData={contractData}
          setContractData={setContractData}
          onSubmit={handleCreateContract}
          onClose={() => setContractModalVisible(false)}
        />
      )}

      {contractsModalVisible && (
        <ContractsModal
          locale={locale}
          contractList={contractList}
          profile={profile}
          token={token}
          openContractView={openContractView}
          onClose={() => setContractsModalVisible(false)}
        />
      )}

      {viewingContract && selectedContract && (
        <ViewContractModal
          selectedContract={selectedContract}
          setSelectedContract={setSelectedContract}
          profile={profile}
          token={token}
          locale={locale}
          openContractView={openContractView}
          onClose={() => setViewingContract(false)}
        />
      )}
    </>
  );
}
